import React, { useState } from 'react';
import { Trash2, Edit, Package, SearchIcon, X, FolderOpen } from 'lucide-react';
import { Button, Input, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Product } from '@/services/apis/products';
import { BaseTable, TableColumn } from '../../base';
import { BaseTableCell } from '../../base/BaseTable';
import ConfirmationModal from '../../base/ConfirmationModal';
import CategorySelector from '../../base/selectors/CategorySelector';
import CategoriesModal from '../CategoriesModal';

interface ProductsTabProps {
  products: Product[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategoryId?: string;
  onCategoryChange: (value: string | undefined) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  deleteModalOpen: boolean;
  selectedProduct: Product | null;
  onDeleteConfirm: () => void;
  onDeleteModalClose: () => void;
  isDeleting: boolean;
  loadMoreRef: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  hasNextPage: boolean;
  onCreate: () => void;
}

const ProductsTab: React.FC<ProductsTabProps> = ({
  products,
  isLoading,
  isFetchingNextPage,
  error,
  searchQuery,
  onSearchChange,
  selectedCategoryId,
  onCategoryChange,
  hasActiveFilters,
  onClearFilters,
  onEdit,
  onDelete,
  deleteModalOpen,
  selectedProduct: _selectedProduct,
  onDeleteConfirm,
  onDeleteModalClose,
  isDeleting,
  loadMoreRef,
  hasNextPage,
  onCreate,
}) => {
  const { t } = useTranslation('admin');
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

  const productColumns: TableColumn[] = [
    { name: t('products.table.columns.name'), uid: 'name', sortable: true },
    { name: t('products.table.columns.category'), uid: 'category', sortable: true },
    { name: t('products.table.columns.status'), uid: 'status', sortable: true },
    { name: t('products.table.columns.createdAt'), uid: 'createdAt', sortable: true },
    { name: t('products.table.columns.actions'), uid: 'actions', sortable: false },
  ];

  const renderCell = (product: Product, columnKey: string) => {
    switch (columnKey) {
      case 'name':
        return (
          <BaseTableCell
            text={product.name}
            description={product.tagline || product.description || ''}
            icon={<Package className="w-5 h-5" />}
          />
        );

      case 'category':
        return <BaseTableCell text={product.category?.name || t('products.placeholders.noCategory')} />;

      case 'status':
        return (
          <BaseTableCell
            text={
              product.status === 'operational'
                ? t('common.status.operational')
                : product.status === 'beta'
                ? t('common.status.beta')
                : t('common.status.maintenance')
            }
          />
        );

      case 'createdAt':
        return (
          <BaseTableCell
            text={
              product.createdAt
                ? new Date(product.createdAt).toLocaleDateString()
                : t('products.placeholders.notSpecified')
            }
          />
        );

      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(product)}
              aria-label={t('products.actions.edit')}
              title={t('products.actions.edit')}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(product)}
              aria-label={t('products.actions.delete')}
              title={t('products.actions.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('products.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('products.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="flat"
            startContent={<FolderOpen className="w-4 h-4" />}
            onPress={() => setIsCategoriesModalOpen(true)}
          >
            {t('products.categories.manageButton')}
          </Button>
          <Button color="primary" onPress={onCreate}>
            {t('products.createButton')}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-between gap-3 items-end flex-wrap">
        <Input
          isClearable
          className="w-full sm:max-w-[44%]"
          placeholder={t('products.search.placeholder')}
          startContent={<SearchIcon />}
          value={searchQuery}
          onClear={() => onSearchChange('')}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3 flex-wrap">
          <div className="w-[150px]">
            <CategorySelector
              value={selectedCategoryId || 'all'}
              onChange={(value) => onCategoryChange(value === 'all' ? undefined : value)}
              placeholder={t('products.table.columns.category')}
              variant="flat"
              label=""
              showAllOption={true}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="flat" onPress={onClearFilters} startContent={<X className="w-4 h-4" />}>
              {t('products.filters.clear')}
            </Button>
          )}
        </div>
      </div>

      {/* Products Table */}
      <BaseTable
        data={products}
        columns={productColumns}
        isLoading={isLoading && products.length === 0}
        loadingMessage={t('products.table.loading')}
        error={error}
        emptyMessage={t('products.table.empty.title')}
        emptyIcon={<Package className="w-12 h-12 text-gray-400" />}
        renderCell={renderCell}
        stickyHeader={true}
        hoverable={true}
        striped={true}
        idField="id"
      />

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner size="lg" />}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={onDeleteConfirm}
        title={t('products.modals.delete.title')}
        message={t('products.modals.delete.message')}
        confirmLabel={t('products.modals.delete.confirm')}
        cancelLabel={t('products.modals.delete.cancel')}
        confirmColor="danger"
        isLoading={isDeleting}
      />

      {/* Categories Management Modal */}
      <CategoriesModal isOpen={isCategoriesModalOpen} onClose={() => setIsCategoriesModalOpen(false)} />
    </div>
  );
};

export default ProductsTab;
